/**
 * Where the application starts.
 *
 * Order matters here. State and routing are installed before anything reads
 * either, the theme is applied before the first paint so the page never
 * flashes the wrong one, and the session is established before the first view
 * that needs it renders.
 */

import { createPinia } from 'pinia'
import { createApp } from 'vue'

import App from '@/App.vue'
import { boot } from '@/app/boot'
import { ConfigurationError } from '@/app/config'
import { router } from '@/app/router'
import { i18n, initialLocale } from '@/i18n'
import { useThemeStore } from '@/stores/theme'

// The typeface, before anything that sets a font-family. Self-hosted rather
// than fetched from a font service: the application then works offline, adds
// no third-party request to every page load, and tells nobody outside this
// network who opened a report. Subset by unicode range, so a French and
// English interface never downloads the Cyrillic or Greek cuts.
import '@fontsource-variable/inter'
import '@fontsource-variable/inter/wght-italic.css'

// Order matters: the design tokens first, then everything built on them.
// Loading a stylesheet that reads a token before the token is defined leaves
// the rule falling back to nothing.
//
// No Bootstrap. Nothing here uses it, and having it loaded was worse than
// useless: it owns `.modal`, `.row` and `.badge`, all of which this interface
// uses for its own things, and its `.modal { display: none }` made every
// dialog in the application invisible. `reset.css` carries the handful of
// Reboot rules that were actually being relied on, `box-sizing` above all.
import '@/assets/theme/tokens.css'
import '@/assets/theme/reset.css'
import '@/assets/theme/main.css'
import '@/assets/theme/buttons.css'
import '@/assets/theme/forms.css'
import '@/assets/theme/tables-finance.css'
import '@/assets/theme/report-builder.css'
import '@/assets/theme/light-theme.css'
import '@/assets/theme/builder.css'

/**
 * Say what is wrong, in the page.
 *
 * A configuration mistake throws while the application is being assembled,
 * before there is anything to render it with. Without this the page is simply
 * blank and the reason is in a console nobody has open, which turns a
 * one-line fix into an afternoon.
 */
function reportFatal(error: unknown): void {
  const reason =
    error instanceof ConfigurationError
      ? error.message
      : 'The application could not start. See the browser console for details.'

  const root = document.getElementById('app')
  if (root) root.textContent = reason
  console.error('[startup]', error)
}

try {
  const app = createApp(App)

  app.use(createPinia())
  app.use(router)
  app.use(i18n)

  // On the document too, so the browser hyphenates and quotes by the right
  // rules. Justified French text hyphenated as English breaks words in the
  // wrong places, and it shows.
  document.documentElement.lang = initialLocale()

  // Before mounting: applying it afterwards means one frame in the wrong
  // theme, which is exactly long enough to be noticed.
  useThemeStore().apply()

  app.mount('#app')

  // After mounting, not before. Sign-in can involve a redirect that takes
  // seconds; leaving the page blank until it resolves makes a working
  // application look broken.
  void boot()
} catch (error) {
  reportFatal(error)
}
